import { useQuery } from "@tanstack/react-query";
import ApiSink from "../api/sink";
import {
  MaterialVolume,
  MaterialProperty,
  LayerMaterials,
} from "../../../smokepit/src/types/MaterialVolumes";

const apiSink = new ApiSink();

/**
 * Frontend-specific types for material volumes.
 * These differ from the backend types because:
 * - nominal_flight_date is a string (after JSON serialization) instead of Date
 * - The response structure is transformed to include current_survey and site_surveys
 *   instead of the backend's site_id and surveys array
 */
export interface SurveyMaterialVolumes {
  survey_id: number;
  survey_label: string;
  nominal_flight_date: string;
  layers: LayerMaterials[];
}

export interface MaterialsVolumesResponse {
  current_survey?: SurveyMaterialVolumes;
  site_surveys?: SurveyMaterialVolumes[];
}

// Re-export shared types for convenience
export type { MaterialVolume, MaterialProperty, LayerMaterials };

export function useMaterialsVolumes(surveyId: number | null) {
  const query = useQuery({
    queryKey: ["materialsVolumes", surveyId],
    queryFn: async () => {
      if (!surveyId) return null;
      const response = await apiSink.getMaterialsVolumes(surveyId);
      if (response.error) {
        console.error(
          `Failed to fetch materials volumes for survey ${surveyId}:`,
          response.message || response.error
        );
        return null; // Treat errors as no data available
      }

      // Unwrap the data property from API response
      const apiData = response.data;
      if (!apiData || !apiData.surveys) {
        console.warn(
          `Unexpected API response structure for survey ${surveyId}:`,
          response
        );
        return null;
      }

      // Transform to expected structure
      const transformed: MaterialsVolumesResponse = {
        site_surveys: apiData.surveys,
        current_survey: apiData.surveys.find(
          (s) => s.survey_id === surveyId
        ),
      };

      return transformed;
    },
    enabled: surveyId !== null,
    staleTime: 1000 * 60, // 60 seconds
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
